import { api } from "@/lib/api";

/**
 * Web Push subscription helpers.
 *
 * Flow on first opt-in:
 *   1. `Notification.requestPermission()` — the OS/browser prompt.
 *   2. Wait for the service worker registration (already registered by
 *      registerServiceWorker() in App boot).
 *   3. Fetch VAPID public key from the backend.
 *   4. sw.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: <VAPID> }).
 *   5. POST the subscription JSON to /api/push/subscribe.
 *
 * The subscription lives on the push service (FCM/APNs/Mozilla) and the
 * browser gives us an opaque `endpoint` URL. We just persist that + the
 * keys — the backend never sees the actual push token.
 */

/** True on browsers that support the full Web Push stack (Chrome, Edge,
 *  Firefox, Safari 16.4+ ON INSTALLED PWA). On iOS Safari in a tab, this
 *  returns false — which is correct; iOS only allows push from PWAs. */
export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

/** Standalone-PWA detection — iOS ONLY delivers push when the coach has
 *  added the site to their home screen. If they're in a regular Safari
 *  tab, requesting permission won't actually work. */
export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  // iOS
  if (window.navigator.standalone === true) return true;
  // Android / Chrome / desktop PWA
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return false;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Returns the active service worker registration, or null. */
async function getSwReg() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) || null;
  } catch {
    return null;
  }
}

/** Returns the CURRENT subscription if the user is already subscribed. */
export async function getCurrentPushSubscription() {
  const reg = await getSwReg();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Full opt-in flow. Returns { ok, reason? }.
 * Throws only on programmer errors; user-denials return { ok: false, reason }.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: "This browser doesn't support push notifications." };
  }
  // iOS Safari won't grant permission unless the site is installed.
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isIos && !isStandalonePWA()) {
    return {
      ok: false,
      reason: "iOS requires installing this app to your Home Screen first. Tap the Share icon → Add to Home Screen, then reopen from your Home Screen.",
    };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "Notifications permission was denied. Enable it from browser settings and try again." };
  }

  const reg = await navigator.serviceWorker.ready;
  const { data: keyResp } = await api.get("/push/vapid-public-key");
  if (!keyResp?.key) {
    return { ok: false, reason: "Server missing VAPID configuration — contact support." };
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResp.key),
      });
    } catch (e) {
      return { ok: false, reason: e?.message || "Couldn't subscribe to push." };
    }
  }

  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return { ok: true };
}

/** Tear down the local subscription + tell the backend to drop it. */
export async function unsubscribeFromPush() {
  const sub = await getCurrentPushSubscription();
  if (!sub) return { ok: true };
  const raw = sub.toJSON();
  try { await sub.unsubscribe(); } catch { /* */ }
  try { await api.post("/push/unsubscribe", { endpoint: raw.endpoint }); } catch { /* */ }
  return { ok: true };
}

/** Trigger a test push from the backend so the coach can verify their
 *  device is actually receiving. */
export async function sendTestPush() {
  const { data } = await api.post("/push/test", {});
  return data;
}
